import PropTypes from "prop-types";
import { useState } from "react";
// @mui
import { alpha } from "@mui/material/styles";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import ButtonGroup from "@mui/material/ButtonGroup";
// components
import Iconify from "src/components/iconify";
import Button from "src/consuman-components/generic/button";
//
import { COLUMN_DRAG_TYPE } from "./header-row";

const GROUP_CHIP_DRAG_TYPE = "application/x-consuman-grid-group";

function GroupPanel({ ctx }) {
  const [over, setOver] = useState(false);
  const grouped = ctx.groupedColumns;

  const dropIndex = event => {
    const chips = Array.from(event.currentTarget.querySelectorAll("[data-group-index]"));
    const index = chips.findIndex(chip => {
      const rect = chip.getBoundingClientRect();

      return event.clientX < rect.left + rect.width / 2;
    });

    return index < 0 ? chips.length : index;
  };

  const handleDragOver = event => {
    const column = ctx.getDraggedColumnConfig();
    const isChip = event.dataTransfer.types.includes(GROUP_CHIP_DRAG_TYPE);

    if (!isChip && (!column || !column.allowGrouping)) return;

    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    setOver(true);
  };

  const handleDrop = event => {
    setOver(false);

    const chipId = event.dataTransfer.getData(GROUP_CHIP_DRAG_TYPE);
    const columnId = chipId || event.dataTransfer.getData(COLUMN_DRAG_TYPE);

    if (!columnId) return;

    event.preventDefault();
    ctx.groupById(columnId, dropIndex(event));
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.75}
      onDragOver={handleDragOver}
      onDragLeave={() => setOver(false)}
      onDrop={handleDrop}
      sx={{
        flex: "1 1 auto",
        minWidth: 0,
        minHeight: 36,
        px: 1,
        borderRadius: 1,
        border: theme => `1px dashed ${over ? theme.palette.primary.main : "transparent"}`,
        bgcolor: theme => (over ? alpha(theme.palette.primary.main, 0.08) : "transparent"),
        overflowX: "auto",
        transition: theme => theme.transitions.create(["background-color", "border-color"]),
      }}
    >
      {grouped.length === 0 ? (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ color: "text.disabled", minWidth: 0 }}
        >
          <Iconify
            icon="mdi:format-list-group"
            width={18}
          />
          <Typography
            variant="body2"
            noWrap
          >
            {ctx.groupPanelEmptyText || ctx.t("gridGroupPanelEmpty")}
          </Typography>
        </Stack>
      ) : (
        grouped.map(({ column, state }, index) => (
          <Stack
            key={column.id}
            direction="row"
            alignItems="center"
            spacing={0.75}
            data-group-index={index}
            draggable
            onDragStart={event => {
              event.dataTransfer.setData(GROUP_CHIP_DRAG_TYPE, column.id);
              event.dataTransfer.setData(COLUMN_DRAG_TYPE, column.id);
              ctx.setDraggedColumn(column.id);
            }}
            onDragEnd={() => ctx.setDraggedColumn(null)}
          >
            {index > 0 ? (
              <Iconify
                icon="eva:arrow-ios-forward-fill"
                width={14}
                sx={{ color: "text.disabled" }}
              />
            ) : null}
            <Chip
              size="small"
              variant="soft"
              color="primary"
              label={column.caption}
              icon={
                <Iconify
                  icon={
                    state.sortOrder === "desc" ? "eva:arrow-downward-fill" : "eva:arrow-upward-fill"
                  }
                  width={14}
                />
              }
              onClick={() => ctx.setSort(column, state.sortOrder === "desc" ? "asc" : "desc")}
              onDelete={() => ctx.ungroup(column)}
              sx={{ cursor: "grab" }}
            />
          </Stack>
        ))
      )}
    </Stack>
  );
}

GroupPanel.propTypes = {
  ctx: PropTypes.object,
};

function ExportButton({ ctx }) {
  const [anchor, setAnchor] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = format => async () => {
    setAnchor(null);
    setBusy(true);

    try {
      await ctx.exportTo(format);
    } finally {
      setBusy(false);
    }
  };

  const labels = {
    xlsx: { icon: "vscode-icons:file-type-excel", text: ctx.t("gridExportExcel") },
    pdf: { icon: "vscode-icons:file-type-pdf2", text: ctx.t("gridExportPdf") },
  };

  return (
    <>
      <Tooltip title={ctx.t("gridExport")}>
        <span>
          <IconButton
            onClick={event => setAnchor(event.currentTarget)}
            disabled={busy}
            aria-label={ctx.t("gridExport")}
          >
            {busy ? (
              <CircularProgress size={20} />
            ) : (
              <Iconify
                icon="mdi:tray-arrow-down"
                width={22}
              />
            )}
          </IconButton>
        </span>
      </Tooltip>
      <Menu
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {ctx.exportFormats
          .filter(format => labels[format])
          .map(format => (
            <MenuItem
              key={format}
              onClick={run(format)}
            >
              <Iconify
                icon={labels[format].icon}
                width={20}
                sx={{ mr: 2, flexShrink: 0 }}
              />
              {labels[format].text}
            </MenuItem>
          ))}
      </Menu>
    </>
  );
}

ExportButton.propTypes = {
  ctx: PropTypes.object,
};

function GroupToggle({ ctx }) {
  return (
    <ButtonGroup sx={{ mr: 0.5 }}>
      <Button
        onClick={() => ctx.setAllExpanded(false)}
        size="small"
        color="primary"
        icon="lets-icons:expand-up-double"
        tooltip={ctx.t("collapse")}
        aria-label={ctx.t("collapse")}
      />
      <Button
        onClick={() => ctx.setAllExpanded(true)}
        size="small"
        color="primary"
        icon="lets-icons:expand-down-double"
        tooltip={ctx.t("expand")}
        aria-label={ctx.t("expand")}
      />
    </ButtonGroup>
  );
}

GroupToggle.propTypes = {
  ctx: PropTypes.object,
};

function renderItem(item, key) {
  if (typeof item.render === "function") return <Box key={key}>{item.render()}</Box>;
  if (typeof item.children === "function") return <Box key={key}>{item.children()}</Box>;
  if (item.children) return <Box key={key}>{item.children}</Box>;

  return null;
}

export default function GridToolbar({ ctx, items }) {
  const builtIn = {
    groupPanel: ctx.groupPanelVisible ? (
      <GroupPanel
        key="groupPanel"
        ctx={ctx}
      />
    ) : null,
    exportButton: ctx.exportEnabled ? (
      <ExportButton
        key="exportButton"
        ctx={ctx}
      />
    ) : null,
    columnChooserButton: ctx.columnChooserEnabled ? (
      <Tooltip
        key="columnChooserButton"
        title={ctx.t("gridColumnChooser")}
      >
        <IconButton
          data-grid-chooser
          onClick={event => ctx.openColumnChooser(event.currentTarget)}
          aria-label={ctx.t("gridColumnChooser")}
        >
          <Iconify
            icon="mdi:table-cog"
            width={22}
          />
        </IconButton>
      </Tooltip>
    ) : null,
  };

  const before = [];
  const after = [];

  if (ctx.groupToggleVisible)
    after.push(
      <GroupToggle
        key="groupToggle"
        ctx={ctx}
      />
    );

  items.forEach((item, index) => {
    const element =
      item.name && item.name in builtIn ? builtIn[item.name] : renderItem(item, `item${index}`);

    if (!element) return;

    if (item.location === "before") before.push(element);
    else after.push(element);
  });

  if (!before.length && !after.length) return null;

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      className="cg-toolbar"
      sx={{ px: 1.5, py: 0.75, minHeight: 52 }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ flex: "1 1 auto", minWidth: 0 }}
      >
        {before}
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{ flexShrink: 0 }}
      >
        {after}
      </Stack>
    </Stack>
  );
}

GridToolbar.propTypes = {
  ctx: PropTypes.object,
  items: PropTypes.array,
};
